import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { getCurrencyMeta } from '../../../../utils/currency';
import { estimateStripeFeeMinor, getStripeFeeConfig } from '../../../../utils/fees';

function formatWhen(value) {
  if (!value) return '';
  try {
    let date;
    if (typeof value?.toDate === 'function') date = value.toDate();
    else if (typeof value === 'number') date = new Date(value);
    else date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function normalizeStatus(entry = {}) {
  const status = String(entry?.status || '').toLowerCase();
  if (
    status === 'cancel_at_period_end'
    || entry?.cancelAtPeriodEnd === true
    || entry?.manualRenewalsStopped === true
    || entry?.autoRenew === false
  ) {
    return 'Cancels at period end';
  }
  if (status === 'active') return 'Active';
  return 'Inactive';
}

function formatMode(entry = {}) {
  return entry?.mode === 'subscription' ? 'Subscription' : entry?.mode === 'manual' ? 'Manual' : 'Unknown';
}

function getRelevantDate(entry = {}) {
  const date = formatWhen(entry?.currentPeriodEnd) || formatWhen(entry?.nextChargeDate) || formatWhen(entry?.renewalDate) || '';
  if (!date) return '';
  if (entry?.mode === 'subscription') return `Ends ${date}`;
  if (normalizeStatus(entry) === 'Cancels at period end') return `Access until ${date}`;
  return `Next charge ${date}`;
}

function estimateNetIncomeMinor(baseAmountMinor, stripeFeeHandling, stripeFeeConfig) {
  const baseAmount = Math.max(0, Math.round(Number(baseAmountMinor || 0)));
  if (baseAmount <= 0) return 0;
  if (stripeFeeHandling === 'add') return baseAmount;
  return Math.max(0, baseAmount - estimateStripeFeeMinor(baseAmount, stripeFeeConfig));
}

function formatMinor(minor, currencyMeta) {
  const amount = Number(minor || 0) / Math.pow(10, currencyMeta.decimals);
  return `${currencyMeta.symbol}${amount.toFixed(currencyMeta.decimals)}`;
}

function formatAmountLimits(boltOn = {}) {
  const checkoutLimit = Number.isFinite(Number(boltOn?.checkoutQuantityLimit)) && Number(boltOn.checkoutQuantityLimit) > 0
    ? Math.floor(Number(boltOn.checkoutQuantityLimit))
    : null;
  const totalLimit = Number.isFinite(Number(boltOn?.totalQuantityLimit)) && Number(boltOn.totalQuantityLimit) > 0
    ? Math.floor(Number(boltOn.totalQuantityLimit))
    : null;

  if (!checkoutLimit && !totalLimit) return 'No limits';
  if (checkoutLimit && totalLimit) return `Checkout ${checkoutLimit} • Total ${totalLimit}`;
  if (checkoutLimit) return `Checkout ${checkoutLimit}`;
  return `Total ${totalLimit}`;
}

export default function BoltOnPurchasersModal({
  isOpen,
  onClose,
  teamId,
  boltOns = [],
  currency = 'GBP',
  memberProfiles = [],
}) {
  const [selectedBoltOnId, setSelectedBoltOnId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [summaryRows, setSummaryRows] = useState([]);
  const [detailRowsByBoltOnId, setDetailRowsByBoltOnId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currencyMeta = useMemo(() => getCurrencyMeta(currency), [currency]);
  const availableBoltOns = useMemo(
    () => (Array.isArray(boltOns) ? boltOns : []).filter((boltOn) => Boolean(boltOn?.id)),
    [boltOns]
  );
  const initialNameMap = useMemo(() => Object.fromEntries(
    (Array.isArray(memberProfiles) ? memberProfiles : []).map((member) => [member?.id, member?.name || member?.id || 'Unknown member'])
  ), [memberProfiles]);
  const selectedBoltOn = useMemo(
    () => summaryRows.find((row) => row.id === selectedBoltOnId) || summaryRows[0] || null,
    [selectedBoltOnId, summaryRows]
  );
  const selectedDetailRows = useMemo(
    () => (selectedBoltOn ? (detailRowsByBoltOnId[selectedBoltOn.id] || []) : []),
    [detailRowsByBoltOnId, selectedBoltOn]
  );
  const filteredDetailRows = useMemo(() => {
    const needle = memberSearch.trim().toLowerCase();
    if (!needle) return selectedDetailRows;
    return selectedDetailRows.filter((row) => String(row.memberName || '').toLowerCase().includes(needle));
  }, [memberSearch, selectedDetailRows]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (availableBoltOns.length === 0) {
      setSelectedBoltOnId('');
      return undefined;
    }
    if (!selectedBoltOnId || !availableBoltOns.some((boltOn) => boltOn.id === selectedBoltOnId)) {
      setSelectedBoltOnId(availableBoltOns[0].id);
    }
    return undefined;
  }, [availableBoltOns, isOpen, selectedBoltOnId]);

  useEffect(() => {
    setMemberSearch('');
  }, [selectedBoltOnId]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;

    let cancelled = false;
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);

    setLoading(true);
    setError('');
    setSummaryRows([]);
    setDetailRowsByBoltOnId({});

    (async () => {
      try {
        const setupSnap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')).catch(() => null);
        const teamSetup = setupSnap?.exists() ? (setupSnap.data() || {}) : {};
        const stripeFeeConfig = getStripeFeeConfig({ currency, teamFeeCfg: teamSetup });

        const boltOnEntryPairs = await Promise.all(availableBoltOns.map(async (boltOn) => {
          const snap = await getDocs(collection(db, 'teams', teamId, 'boltOns', boltOn.id, 'members'));
          return [boltOn, snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))];
        }));
        if (cancelled) return;

        const missingUserIds = Array.from(new Set(
          boltOnEntryPairs.flatMap(([, entries]) => entries.map((entry) => (
            typeof entry?.userId === 'string' && entry.userId.trim() ? entry.userId.trim() : entry?.id
          )))
            .filter((userId) => Boolean(userId) && !initialNameMap[userId])
        ));

        const fetchedUsers = await Promise.all(missingUserIds.map(async (userId) => {
          const userSnap = await getDoc(doc(db, 'users', userId)).catch(() => null);
          const userData = userSnap?.exists() ? (userSnap.data() || {}) : {};
          const displayName = userData.displayName
            || `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
            || userData.username
            || userId;
          return [userId, displayName];
        }));
        if (cancelled) return;

        const namesById = {
          ...initialNameMap,
          ...Object.fromEntries(fetchedUsers),
        };

        const nextDetailRowsByBoltOnId = {};
        const nextSummaryRows = boltOnEntryPairs.map(([boltOn, entries]) => {
          const stripeFeeHandling = boltOn?.stripeFeeHandling === 'add' ? 'add' : 'deduct';
          const detailRows = entries.map((entry) => {
            const userId = typeof entry?.userId === 'string' && entry.userId.trim() ? entry.userId.trim() : entry?.id;
            const quantity = Math.max(1, Number(entry?.quantity || 1));
            const unitAmount = Math.max(0, Number(entry?.unitAmount || boltOn?.amount || 0));
            const baseAmountMinor = unitAmount * quantity;
            return {
              id: entry?.id || userId,
              memberName: namesById[userId] || userId || 'Unknown member',
              status: normalizeStatus(entry),
              mode: formatMode(entry),
              quantity,
              unitAmount,
              estimatedIncomeMinor: estimateNetIncomeMinor(baseAmountMinor, stripeFeeHandling, stripeFeeConfig),
              relevantDate: getRelevantDate(entry),
            };
          }).sort((a, b) => {
            const statusRank = { Active: 0, 'Cancels at period end': 1, Inactive: 2 };
            return (statusRank[a.status] ?? 9) - (statusRank[b.status] ?? 9)
              || b.quantity - a.quantity
              || a.memberName.localeCompare(b.memberName);
          });

          nextDetailRowsByBoltOnId[boltOn.id] = detailRows;

          return {
            id: boltOn.id,
            name: boltOn?.name || 'Bolt-on',
            purchasers: detailRows.length,
            totalQuantity: detailRows.reduce((sum, row) => sum + row.quantity, 0),
            estimatedIncomeMinor: detailRows.reduce((sum, row) => sum + row.estimatedIncomeMinor, 0),
            amountLimits: formatAmountLimits(boltOn),
          };
        }).sort((a, b) => b.purchasers - a.purchasers || a.name.localeCompare(b.name));

        setDetailRowsByBoltOnId(nextDetailRowsByBoltOnId);
        setSummaryRows(nextSummaryRows);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not load bolt-on purchaser detail.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [availableBoltOns, currency, initialNameMap, isOpen, onClose, teamId]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content bolt-on-insights-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Bolt-on purchaser insights"
      >
        <div className="bolt-on-insights-modal__header">
          <div>
            <h2 className="modal-title">Bolt-on purchaser insights</h2>
            <p className="bolt-on-insights-modal__subtitle">
              Review all bolt-ons first, then select one to see who has purchased it.
            </p>
          </div>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="text-muted">Loading purchaser detail…</p>
        ) : error ? (
          <p className="modal-error">{error}</p>
        ) : (
          <>
            <section className="bolt-on-insights-panel">
              <h3 className="bolt-on-insights-panel__title">Bolt-ons</h3>
              <div className="bolt-on-insights-modal__table-wrap">
                <table className="bolt-on-insights-table">
                  <thead>
                    <tr>
                      <th>Bolt-on</th>
                      <th>Purchasers</th>
                      <th>Quantity</th>
                      <th>Amount limits</th>
                      <th>Estimated income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-muted bolt-on-insights-table__empty">No bolt-ons to review.</td>
                      </tr>
                    ) : summaryRows.map((row) => (
                      <tr
                        key={row.id}
                        className={selectedBoltOn?.id === row.id ? 'bolt-on-insights-table__row--selected' : ''}
                        onClick={() => setSelectedBoltOnId(row.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td data-label="Bolt-on">{row.name}</td>
                        <td data-label="Purchasers">{row.purchasers}</td>
                        <td data-label="Quantity">{row.totalQuantity}</td>
                        <td data-label="Amount limits">{row.amountLimits}</td>
                        <td data-label="Estimated income">{formatMinor(row.estimatedIncomeMinor, currencyMeta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bolt-on-insights-panel">
              <div className="bolt-on-insights-toolbar">
                <h3 className="bolt-on-insights-panel__title">
                  {selectedBoltOn ? `Purchasers for ${selectedBoltOn.name}` : 'Purchasers'}
                </h3>
                <input
                  type="search"
                  className="bolt-on-insights-search"
                  placeholder="Search member names"
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                />
              </div>
              <div className="bolt-on-insights-modal__table-wrap">
                <table className="bolt-on-insights-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Status</th>
                      <th>Mode</th>
                      <th>Quantity</th>
                      <th>Unit amount</th>
                      <th>Estimated income</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBoltOn == null ? (
                      <tr>
                        <td colSpan="7" className="text-muted bolt-on-insights-table__empty">Select a bolt-on above.</td>
                      </tr>
                    ) : selectedDetailRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-muted bolt-on-insights-table__empty">Nobody has purchased this bolt-on yet.</td>
                      </tr>
                    ) : filteredDetailRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-muted bolt-on-insights-table__empty">No members match that search.</td>
                      </tr>
                    ) : filteredDetailRows.map((row) => (
                      <tr key={row.id}>
                        <td data-label="Member">{row.memberName}</td>
                        <td data-label="Status">{row.status}</td>
                        <td data-label="Mode">{row.mode}</td>
                        <td data-label="Quantity">{row.quantity}</td>
                        <td data-label="Unit amount">{formatMinor(row.unitAmount, currencyMeta)}</td>
                        <td data-label="Estimated income">{formatMinor(row.estimatedIncomeMinor, currencyMeta)}</td>
                        <td data-label="Date">{row.relevantDate || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
